// SPDX-License-Identifier: LicenseRef-MMS-WIRE
// Copyright (c) 2016-2026 Metal Management Solutions (Pty) Ltd

import {EnvironmentProviders} from "@angular/core";
import {
    AutoRefreshTokenService,
    createInterceptorCondition,
    INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
    IncludeBearerTokenCondition,
    provideKeycloak,
    UserActivityService,
    withAutoRefreshToken
} from "keycloak-angular";

const sameHostCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
    urlPattern: new RegExp(`^.*$`),
});

/** Gets the realm and keycloak URL for auth redirect
 *
 * Note: This **must** be synchronous - cannot load WIRE before init keycloak
 */
export function getWireConfig() {
    const request = new XMLHttpRequest();
    request.open('GET', '/api/realm_config', false); // `false` makes the request synchronous
    request.send(null);
    try {
        return JSON.parse(request.response);
    } catch (error) {
        console.error("Failed to parse realm config, with error:", error, "\nResponse data:", request.response, "\nResponse status:", request.status);
        if (confirm("System failed to initialize authentication configuration.\n\nReload?")) {
            window.location.href = window.location.origin;
        }
    }
}

export const provideKeycloakAngular = (): EnvironmentProviders => {
    /**
     * Notes on token refreshing.
     * The short lived access_token is refreshed continuously on right before a request is made with an expired access token.
     * The refresh token has a fixed lifespan and needs a login
     * */

    let realm_config = getWireConfig();
    const realm = realm_config['realm'];
    const clientId = realm_config['client_id'];
    const keycloak_url = realm_config['url'];

    return provideKeycloak({
        config: {
            realm: realm,
            url: keycloak_url,
            clientId: clientId
        },
        initOptions: {
            enableLogging: true,
            onLoad: 'login-required', // Either user `login-required` here or do redirects to keycloak.login in the auth guard!
            silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
            scope: 'openid profile email',
        },
        features: [
            withAutoRefreshToken({
                // A simple workaround to single-logout, could be to use BroadcastChannel's to keep all tabs notified of any activity.
                onInactivityTimeout: 'none',
                sessionTimeout: 300000,
            })
        ],
        providers: [
            AutoRefreshTokenService,
            UserActivityService,
            {
                provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
                useValue: [sameHostCondition]
            }
        ]
    });
};
